import React, { useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import HeaderWithSearch from "./HeaderWithSearch";
import "./NotFound.css";

const STARS = Array.from({ length: 26 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: 2 + Math.random() * 3,
  depth: 0.2 + Math.random() * 1.8,
  delay: Math.random() * 4,
}));

export default function NotFound() {
  const pageRef = useRef(null);
  const frame = useRef(0);

  const applyPointer = useCallback((clientX, clientY) => {
    const el = pageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    // Normalized -1..1 offset from the center.
    const nx = (px / rect.width - 0.5) * 2;
    const ny = (py / rect.height - 0.5) * 2;

    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      el.style.setProperty("--px", `${px}px`);
      el.style.setProperty("--py", `${py}px`);
      el.style.setProperty("--nx", nx.toFixed(4));
      el.style.setProperty("--ny", ny.toFixed(4));
    });
  }, []);

  const handleMouseMove = useCallback(
    (e) => applyPointer(e.clientX, e.clientY),
    [applyPointer]
  );

  const handleTouchMove = useCallback(
    (e) => {
      const t = e.touches[0];
      if (t) applyPointer(t.clientX, t.clientY);
    },
    [applyPointer]
  );

  const resetPointer = useCallback(() => {
    const el = pageRef.current;
    if (!el) return;
    el.style.setProperty("--nx", "0");
    el.style.setProperty("--ny", "0");
  }, []);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  return (
    <>
      <HeaderWithSearch />
      <main
        ref={pageRef}
        className="nf-page"
        onMouseMove={handleMouseMove}
        onMouseLeave={resetPointer}
        onTouchMove={handleTouchMove}
      >
        {/* Cursor-following spotlight */}
        <div className="nf-spotlight" aria-hidden="true" />

        {/* Parallax stars */}
        <div className="nf-stars" aria-hidden="true">
          {STARS.map((s) => (
            <span
              key={s.id}
              className="nf-star"
              style={{
                left: `${s.left}%`,
                top: `${s.top}%`,
                width: `${s.size}px`,
                height: `${s.size}px`,
                animationDelay: `${s.delay}s`,
                "--depth": s.depth,
              }}
            />
          ))}
        </div>

        {/* Parallax gradient blobs */}
        <span className="nf-blob nf-blob-1" aria-hidden="true" />
        <span className="nf-blob nf-blob-2" aria-hidden="true" />
        <span className="nf-blob nf-blob-3" aria-hidden="true" />

        <section className="nf-card">
          <span className="nf-emoji" role="img" aria-label="rocket">🚀</span>
          <h1 className="nf-code" data-text="404">404</h1>
          <h2 className="nf-title">Lost in space</h2>
          <p className="nf-text">
            Oops! The page you're looking for drifted off into orbit. Move your
            mouse to steer through the stars, then head back to safety.
          </p>

          <div className="nf-actions">
            <Link to="/" className="nf-btn nf-btn-primary">🏠 Back to Home</Link>
            <Link to="/games" className="nf-btn nf-btn-ghost">🎮 Play a Game</Link>
          </div>

          <div className="nf-links">
            <p className="nf-links-label">Popular destinations</p>
            <div className="nf-chips">
              <Link to="/games" className="nf-chip">Games</Link>
              <Link to="/math" className="nf-chip">Math</Link>
              <Link to="/pop" className="nf-chip">Piano Covers</Link>
              <Link to="/meals" className="nf-chip">Recipes</Link>
              <Link to="/abacus" className="nf-chip">Abacus</Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
